import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn, Avatar, AvatarImage, AvatarFallback } from "@tellescope/ui";
import { formatInitials } from "@tellescope/utils";
import { useVideoCalling } from "../hooks/useVideoCallingContext";
import { useTrackAttachment } from "../hooks/useTrackAttachment";
import type { LocalPreviewProps } from "../types";

/**
 * LocalPreview - Shows the local camera preview in the waiting room.
 *
 * Attaches the local video track to a video element for preview before joining.
 */
export function LocalPreview({ localUser, className }: LocalPreviewProps) {
  const { t } = useTranslation("videoCalling");
  const videoCalling = useVideoCalling();

  const { localVideoTrack, isVideoEnabled } = videoCalling;
  const videoRef = useTrackAttachment<HTMLVideoElement>(localVideoTrack);

  // Get initials from user name, or fall back to "You"
  const initials = useMemo(() => {
    if (localUser?.fname || localUser?.lname) {
      return formatInitials(localUser);
    }
    return t("You");
  }, [localUser, t]);

  return (
    <div className={cn("relative w-full aspect-video bg-gray-900 rounded-lg", className)}>
      {/* Video element */}
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        className={cn(
          "w-full h-full object-cover scale-x-[-1]", // Mirror for preview
          !isVideoEnabled && "hidden"
        )}
      />

      {/* Placeholder when video is disabled */}
      {(!localVideoTrack || !isVideoEnabled) && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
          <div className="relative w-1/2 max-w-[200px] aspect-square">
            <Avatar className="w-full h-full shadow-lg">
              {localUser?.avatar && <AvatarImage src={localUser.avatar} alt={initials} />}
              <AvatarFallback className="bg-gray-200 text-gray-700 text-[4rem] font-medium">{initials}</AvatarFallback>
            </Avatar>
            {/* Online status indicator */}
            <div className="absolute top-1 right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-gray-800" />
          </div>
        </div>
      )}

      {/* Label */}
      <div className="absolute bottom-2 left-2 pointer-events-none">
        <div className="bg-black/60 px-2 py-1 rounded text-white text-sm">{t("You")}</div>
      </div>
    </div>
  );
}
